/**
 * Jurisdiction Fail Protocol — office fold (JURISDICTION-FAIL-PROTOCOL.md).
 *
 * Rendered INSIDE the failing device's row on the Results card (Issue #768
 * folded the old standalone card into the row, so the protocol reads as part
 * of that device's story). The office's sprint lives here: review the
 * auto-drafted repair quote, fire it (vendor thread or customer send), record
 * the approved NTE the minute it lands, or pause/decline when the answer is
 * slow or no. Paused wears the loud "WATER OFF — AWAITING NTE" badge.
 *
 * Approval flows back to the ORIGINATING job — it never creates a new one.
 * "Create return-visit job" below is the explicit office choice for repairs
 * that genuinely need a second trip.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

export interface OfficeProtocol {
    id: string;
    job_id: string;
    asset_id: string;
    status: "active" | "paused" | "declined" | "released";
    device: string;
    provider_name: string | null;
    water_off_at: string | null;
    city_called_at: string | null;
    city_contact_name: string | null;
    city_reference: string | null;
    vendor_call_required: boolean;
    vendor_called_at: string | null;
    quote_sent_at: string | null;
    nte_approved: boolean;
    repair_completed_at: string | null;
    retest_passed_at: string | null;
    water_on_at: string | null;
    missing: string[];
    estimate_id: string | null;
    estimate_status: string | null;
    estimate_number: string | null;
    estimate_total: number;
    nte_amount: number | null;
    nte_reference: string | null;
    nte_approver: string | null;
    nte_approved_at: string | null;
    paused_at: string | null;
    declined_at: string | null;
    declined_reason: string | null;
    override_at: string | null;
    override_reason: string | null;
    return_visit_job_id: string | null;
}

const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

function errMessage(err: unknown): string {
    return err instanceof ApiError && typeof (err.body as any)?.message === "string"
        ? (err.body as any).message
        : "That didn't save — try again.";
}

function ProtocolRow({ p }: { p: OfficeProtocol }) {
    const qc = useQueryClient();
    const [nteAmount, setNteAmount] = useState("");
    const [nteApprover, setNteApprover] = useState("");
    const [nteReference, setNteReference] = useState("");
    const [declineReason, setDeclineReason] = useState("");
    const [showDecline, setShowDecline] = useState(false);
    const [overrideReason, setOverrideReason] = useState("");
    const [showOverride, setShowOverride] = useState(false);

    const refresh = () => {
        qc.invalidateQueries({ queryKey: ["fail-protocol-office", p.job_id] });
        qc.invalidateQueries({ queryKey: ["job", p.job_id] });
    };

    const sendQuote = useMutation({
        mutationFn: () => api.post(`/api/fail-protocol/${p.id}/send-quote`, {}),
        onSuccess: () => {
            toast.success(
                p.vendor_call_required
                    ? "Quote marked sent to the vendor — record the NTE when their answer lands."
                    : "Quote marked sent to the customer.",
            );
            refresh();
        },
        onError: (e) => toast.error(errMessage(e)),
    });
    const recordNte = useMutation({
        mutationFn: () =>
            api.post<{ return_visit_job_id: string | null }>(`/api/fail-protocol/${p.id}/record-nte`, {
                amount: Number(nteAmount),
                approver: nteApprover.trim(),
                reference: nteReference.trim() || undefined,
            }),
        onSuccess: () => {
            toast.success("NTE recorded — the tech is clear to repair on this job. They've been notified.");
            refresh();
        },
        onError: (e) => toast.error(errMessage(e)),
    });
    const createReturnVisit = useMutation({
        mutationFn: () =>
            api.post<{ return_visit_job_id: string }>(`/api/fail-protocol/${p.id}/create-return-visit`, {}),
        onSuccess: () => {
            toast.success("Return-visit job created — schedule it from the jobs board.");
            refresh();
        },
        onError: (e) => toast.error(errMessage(e)),
    });
    const pause = useMutation({
        mutationFn: () => api.post(`/api/fail-protocol/${p.id}/pause`, {}),
        onSuccess: () => {
            toast.success("Protocol paused — the job wears WATER OFF — AWAITING NTE until the approval lands.");
            refresh();
        },
        onError: (e) => toast.error(errMessage(e)),
    });
    const decline = useMutation({
        mutationFn: () => api.post(`/api/fail-protocol/${p.id}/decline`, { reason: declineReason.trim() }),
        onSuccess: () => {
            toast.success("Recorded as declined — the device stays off and red-tagged.");
            setShowDecline(false);
            refresh();
        },
        onError: (e) => toast.error(errMessage(e)),
    });
    const override = useMutation({
        mutationFn: () => api.post(`/api/fail-protocol/${p.id}/override-complete`, { reason: overrideReason.trim() }),
        onSuccess: () => {
            toast.success("Protocol released by override — the device is un-tagged and the reason is logged.");
            setShowOverride(false);
            refresh();
        },
        onError: (e) => toast.error(errMessage(e)),
    });

    const nteValid = Number(nteAmount) > 0 && nteApprover.trim().length > 0;

    return (
        <div className="space-y-2 rounded-lg border border-red-300 p-3 dark:border-red-500/40">
            <div className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{p.device}</span>
                {p.status === "paused" && (
                    <Badge variant="destructive" className="uppercase">
                        Water off — awaiting NTE
                    </Badge>
                )}
                {p.status === "declined" && (
                    <Badge variant="destructive" className="uppercase">
                        Declined
                    </Badge>
                )}
                {p.status === "released" && (
                    <Badge variant="success" className="uppercase">
                        {p.override_at ? "Released — office override" : "Released"}
                    </Badge>
                )}
            </div>

            {/* Field-side chain, read-only for the office. */}
            <p className="text-xs text-muted-foreground">
                Water off {p.water_off_at ? "✓" : "— pending"} · {p.provider_name ?? "City"} notified{" "}
                {p.city_called_at
                    ? `✓ (${p.city_contact_name ?? "?"}${p.city_reference ? `, ref ${p.city_reference}` : ""})`
                    : "— pending"}
                {p.vendor_call_required && <> · Vendor called {p.vendor_called_at ? "✓" : "— pending"}</>}
                {" · "}Repair {p.repair_completed_at ? "✓" : "—"} · Retest {p.retest_passed_at ? "✓" : "—"} · Water back
                on {p.water_on_at ? "✓" : "—"}
            </p>

            {p.status === "declined" && p.declined_reason && (
                <p className="text-xs text-red-700 dark:text-red-300">Declined: {p.declined_reason}</p>
            )}

            {p.override_at && p.override_reason && (
                <p className="text-xs text-emerald-700 dark:text-emerald-300">
                    Override — work verified complete: {p.override_reason}
                </p>
            )}

            {/* The quote. */}
            {p.estimate_id ? (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                    <Link
                        to={`/commercial/${p.estimate_id}?type=estimate`}
                        className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                    >
                        Quote {p.estimate_number ?? ""} — {money(p.estimate_total)}
                        <ExternalLink className="h-3 w-3" />
                    </Link>
                    <span className="text-xs text-muted-foreground">
                        {p.quote_sent_at
                            ? `Sent — waiting on ${p.vendor_call_required ? "the vendor's NTE" : "the customer"}.`
                            : "Auto-drafted — review it, then fire it."}
                    </span>
                </div>
            ) : (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                    No quote attached — the auto-draft couldn't price this repair. Draft one from the job and link it
                    manually.
                </p>
            )}

            {p.nte_approved_at ? (
                <div className="space-y-2">
                    <p className="flex items-center gap-1 text-sm text-emerald-700 dark:text-emerald-300">
                        <CheckCircle2 className="h-4 w-4" />
                        NTE approved{p.nte_amount != null ? ` — ${money(p.nte_amount)}` : ""} by {p.nte_approver ?? "?"}
                        {p.nte_reference ? ` (ref ${p.nte_reference})` : ""} — the tech proceeds on this job.
                    </p>
                    {!p.return_visit_job_id && !p.water_on_at && p.status !== "declined" && (
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={createReturnVisit.isPending}
                            onClick={() => createReturnVisit.mutate()}
                        >
                            {createReturnVisit.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                            Create return-visit job — repair needs another trip
                        </Button>
                    )}
                </div>
            ) : (
                (p.status === "active" || p.status === "paused") && (
                    <div className="space-y-2">
                        {!p.quote_sent_at && p.estimate_id && (
                            <Button size="sm" disabled={sendQuote.isPending} onClick={() => sendQuote.mutate()}>
                                {sendQuote.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                                {p.vendor_call_required ? "Send quote to vendor" : "Mark quote sent to customer"}
                            </Button>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                            <Input
                                placeholder="Approved amount"
                                inputMode="decimal"
                                value={nteAmount}
                                onChange={(e) => setNteAmount(e.target.value)}
                                className="h-9 w-36"
                            />
                            <Input
                                placeholder="Who approved it?"
                                value={nteApprover}
                                onChange={(e) => setNteApprover(e.target.value)}
                                className="h-9 w-44"
                            />
                            <Input
                                placeholder="NTE / PO ref (optional)"
                                value={nteReference}
                                onChange={(e) => setNteReference(e.target.value)}
                                className="h-9 w-44"
                            />
                            <Button
                                size="sm"
                                disabled={!nteValid || recordNte.isPending}
                                onClick={() => recordNte.mutate()}
                            >
                                {recordNte.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                                Record approved NTE
                            </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {p.status === "active" && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={pause.isPending}
                                    onClick={() => pause.mutate()}
                                >
                                    {pause.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                                    Pause — answer is slow, crew moves on
                                </Button>
                            )}
                            {!showDecline ? (
                                <Button size="sm" variant="ghost" onClick={() => setShowDecline(true)}>
                                    Quote declined…
                                </Button>
                            ) : (
                                <div className="flex flex-wrap items-center gap-2">
                                    <Input
                                        placeholder="Why was it declined?"
                                        value={declineReason}
                                        onChange={(e) => setDeclineReason(e.target.value)}
                                        className="h-9 w-64"
                                    />
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        disabled={declineReason.trim().length < 3 || decline.isPending}
                                        onClick={() => decline.mutate()}
                                    >
                                        {decline.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                                        Record decline
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )
            )}

            {/* Third exit — distinct from pause (waiting on an answer) and
                decline (quote dead): the office VERIFIED the work is done
                (paper retest, vendor confirmation) and releases the device.
                Available pre- or post-approval while the chain is open. */}
            {(p.status === "active" || p.status === "paused") &&
                (!showOverride ? (
                    <Button size="sm" variant="ghost" onClick={() => setShowOverride(true)}>
                        Override — work verified complete…
                    </Button>
                ) : (
                    <div className="flex flex-wrap items-center gap-2">
                        <Input
                            placeholder="How was the work verified? (required)"
                            value={overrideReason}
                            onChange={(e) => setOverrideReason(e.target.value)}
                            className="h-9 w-72"
                        />
                        <Button
                            size="sm"
                            disabled={overrideReason.trim().length < 3 || override.isPending}
                            onClick={() => override.mutate()}
                        >
                            {override.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                            Release protocol
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setShowOverride(false)}>
                            Cancel
                        </Button>
                    </div>
                ))}

            {p.return_visit_job_id && (
                <p className="text-sm">
                    <Link
                        to={`/jobs/${p.return_visit_job_id}`}
                        className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                    >
                        Return visit booked — open the repair job
                        <ExternalLink className="h-3 w-3" />
                    </Link>
                </p>
            )}
        </div>
    );
}

/** The job's fail protocols, office-gated. Empty (and no fetch) when the
 *  viewer lacks finance access — the server route is gated the same way. */
export function useJobFailProtocols(jobId: string): OfficeProtocol[] {
    const { can } = useAuth();
    const allowed = can("finance.view");
    const protocols = useQuery({
        queryKey: ["fail-protocol-office", jobId],
        queryFn: () => api.get<{ protocols: OfficeProtocol[] }>(`/api/fail-protocol/job/${jobId}`),
        enabled: allowed && !!jobId,
    });
    return allowed ? (protocols.data?.protocols ?? []) : [];
}

/** The in-row fold: one device's protocol, rendered inside its Results row.
 *  (Replaces the standalone FailProtocolCard — Issue #768 region J5.) */
export function FailProtocolFold({ protocol }: { protocol: OfficeProtocol }) {
    return (
        <div className="mt-2" data-testid="fail-protocol-fold">
            <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-red-700 dark:text-red-300">
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                Jurisdiction fail protocol — {protocol.provider_name ?? "protocol city"}
            </p>
            <ProtocolRow p={protocol} />
        </div>
    );
}
